/**
 * Payment API Service - Single source of truth for payment-related API calls.
 *
 * Consolidates duplicate fetch logic from:
 * - BuyTokensModal.tsx
 * - InsufficientTokensModal.tsx
 * - PricingPage.tsx
 */

import { extractResponseErrorMessage, getAuthHeaders } from '../utils/httpUtils';
import { buildCloudApiUrl } from './urlResolver';

// ============================================================================
// Types
// ============================================================================

export interface TokenPackage {
  id: string;
  name: string;
  tokens: number;
  price_usd: number;
  price_per_1000: number;
  discount_percent: number;
  savings_usd: number;
  popular: boolean;
  available?: boolean;
}

export interface CurrencyOption {
  code: string;
  chain?: string;
}

export interface PackagesResponse {
  packages: TokenPackage[];
  currencies: CurrencyOption[];
}

export interface PaymentDetails {
  payment_address: string;
  payment_amount: string;
  payment_currency: string;
  chain: string;
  package_name: string;
  package_tokens: number;
  price_usd: number;
}

export interface CheckoutRequest {
  packageId: string;
  authToken: string;
  successUrl: string;
  cancelUrl: string;
  currency?: string;
  promoCode?: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch available token packages and supported payment currencies.
 */
export async function fetchPackages(): Promise<PackagesResponse> {
  const response = await fetch(buildCloudApiUrl('/api/v1/payments/packages'));
  const data = await response.json();

  if (!response.ok) {
    throw new Error(extractResponseErrorMessage(data) || 'Failed to load packages');
  }

  const packages = data.data.packages as TokenPackage[];

  let currencies: CurrencyOption[] = [];
  if (data.data.currencies_detail) {
    currencies = data.data.currencies_detail as CurrencyOption[];
  } else if (data.data.supported_currencies) {
    currencies = (data.data.supported_currencies as string[]).map((code) => ({ code }));
  }

  return { packages, currencies };
}

/**
 * Create a crypto payment checkout session.
 * Returns payment details (address + amount) for the user to complete.
 */
export async function createCheckout(request: CheckoutRequest): Promise<PaymentDetails> {
  const response = await fetch(buildCloudApiUrl('/api/v1/payments/checkout'), {
    method: 'POST',
    headers: getAuthHeaders(request.authToken),
    body: JSON.stringify({
      package_id: request.packageId,
      success_url: request.successUrl,
      cancel_url: request.cancelUrl,
      currency: request.currency,
      ...(request.promoCode ? { promo_code: request.promoCode } : {}),
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(extractResponseErrorMessage(data) || 'Failed to create payment');
  }

  return data.data as PaymentDetails;
}
